import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Home as HomeIcon, Users, CheckCircle2, ChevronLeft, ChevronRight } from 'lucide-react';
import arcoAmaiur from '../assets/arco-amaiur-pueblo.webp';
import gatoCasero from '../assets/gato-casero.webp';
import pradosBaztan from '../assets/prados-baztan.webp';
import pradoOvejas from '../assets/prado-ovejas.webp';
import iglesiaSantiago from '../assets/iglesia-santiago-itxasperri.webp';
import plazaPueblo from '../assets/plaza-pueblo-baztan.webp';
import paisajeBaztan from '../assets/paisaje-baztan.webp';
import burraJardin from '../assets/burra-jardin.webp';
// 1. IMPORTACIÓN DE TUS IMÁGENES LOCALES
import imgFachada from '../assets/fachada-graciosenea.webp';
import imgCocina from '../assets/cocina-Graciosenea.webp';
import imgDormitorioDoble from '../assets/dormitorio-doble.webp';
import imgDormitorioMatrimonio from '../assets/dormitorio-matrimonio.webp';
import imgBodega from '../assets/bodega-botellas-cubas.webp';
import imgSalaCuadros from '../assets/sala-cuadros.webp';
import imgBaño from '../assets/sala-de-bano-adaptado.webp';
import imgPianoCoworking from '../assets/sala-piano-chimenea-coworking.webp';
import imgSillas from '../assets/sillas-sala-principal.webp';
import imgVestidor from '../assets/vestidor-espejo-vintage.webp';

const Turismo = () => {
  // 1. Lista de imágenes
  // 2. Estados
  const [currentSlide, setCurrentSlide] = useState(0); // Para las fotos del entorno
  const [isPaused, setIsPaused] = useState(false);
  const [activeImage, setActiveImage] = useState(0);   // Para la galería de la casa
  const fotosEntorno = [
    { src: arcoAmaiur, alt: "Arco de entrada al pueblo de Amaiur, Valle de Baztán" },
    { src: gatoCasero, alt: "Gato doméstico en el caserío Graciosenea" },
    { src: pradoOvejas, alt: "Ovejas latxas pastando en los prados de Graciosenea" },
    { src: iglesiaSantiago, alt: "Iglesia de Santiago Itxasperri, patrimonio local" },
    { src: plazaPueblo, alt: "Plaza típica de un pueblo en el corazón del Baztán" },
    { src: paisajeBaztan, alt: "Vistas panorámicas de las montañas del Pirineo Navarro" },
    { src: burraJardin, alt: "Burrita en el jardín de la casa rural Graciosenea" }
  ];
  const galleryImages = [
      { url: imgFachada, alt: 'Fachada Graciosenea' },
      { url: imgSalaCuadros, alt: 'Segunda sala' },
      { url: imgPianoCoworking, alt: 'Sala de estar y coworking' },
      { url: imgCocina, alt: 'Cocina equipada' },
      { url: imgDormitorioDoble, alt: 'Dormitorio Doble' },
      { url: imgDormitorioMatrimonio, alt: 'Dormitorio Principal' },
      { url: imgBaño, alt: 'Baño adaptado grande' },
      { url: imgVestidor, alt: 'Vestidor vintage' },
      { url: imgBodega, alt: 'Bodega histórica' },
      { url: imgSillas, alt: 'Zona de comedor' }
    ];


  // 3. Funciones (Usamos useCallback para el autoplay)
  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev === fotosEntorno.length - 1 ? 0 : prev + 1));
  }, [fotosEntorno.length]);

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? fotosEntorno.length - 1 : prev - 1));
  };

  // 4. Efecto de Autoplay
  useEffect(() => {
    let interval;
    if (!isPaused) {
      interval = setInterval(() => {
        nextSlide();
      }, 5000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [nextSlide, isPaused]);

  // Aquí empieza tu return (div className="min-h-screen...)
  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={pradosBaztan}
            alt="Vistas de los prados verdes del Valle de Baztán desde Graciosenea"
            className="w-full h-full object-cover"
            fetchpriority="high"
          />
          <div className="absolute inset-0 bg-black/50" />
        </div>
        <div className="relative z-10 text-center text-white px-6">
          <h1
            className="text-5xl md:text-6xl font-bold mb-4 text-[#FDFFD2]"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Turismo Rural
          </h1>
          <p className="text-xl md:text-2xl">Descanso en el Valle de Baztán</p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 bg-[#F5F2ED]">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2
              className="text-4xl font-bold mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Alojamiento Tradicional
            </h2>
            <p className="text-lg text-[#5A5F4D]">
              Disfruta de un descanso auténtico en nuestra casa señorial del siglo XX.
              Desayuno incluido en todas las modalidades de alojamiento.
            </p>
          </div>
        </div>
      </section>
      {/* Gallery */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold mb-12 text-center" style={{ fontFamily: "'Playfair Display', serif" }}>Galería</h2>
          <div className="max-w-6xl mx-auto">
            <div className="mb-6 rounded-lg overflow-hidden shadow-xl">
              <img src={galleryImages[activeImage].url} alt={galleryImages[activeImage].alt} className="w-full h-[500px] object-cover" />
            </div>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
              {galleryImages.map((image, index) => (
                <button 
                  key={index} 
                  onClick={() => setActiveImage(index)} 
                  className={`rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-all ${activeImage === index ? 'ring-4 ring-[#9B2C2C]' : ''}`}
                >
                  <img src={image.url} alt={image.alt} className="w-full h-24 object-cover" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>
      {/* Sección del Carrusel con Autoplay */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-8 text-center" style={{ fontFamily: "'Playfair Display', serif" }}>
            Explora los alrededores de Graciosenea
          </h2>
          
          <div 
            className="relative max-w-4xl mx-auto group"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            {/* Contenedor de Imágenes */}
            <div className="relative h-[400px] md:h-[500px] overflow-hidden rounded-xl shadow-2xl bg-gray-200">
              {fotosEntorno.map((foto, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                    index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
                  }`}
                >
                  <img
                    src={foto.src}
                    alt={foto.alt}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>

            {/* Flechas de Navegación */}
            <button
              onClick={prevSlide}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-white/80 p-3 rounded-full text-gray-800 hover:bg-white transition-all shadow-md md:opacity-0 md:group-hover:opacity-100"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-white/80 p-3 rounded-full text-gray-800 hover:bg-white transition-all shadow-md md:opacity-0 md:group-hover:opacity-100"
            >
              <ChevronRight size={24} />
            </button>

            {/* Indicadores inferiores (Dots) */}
            <div className="flex justify-center gap-3 mt-6">
              {fotosEntorno.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`h-2.5 rounded-full transition-all duration-300 ${
                    index === currentSlide ? 'bg-[#9B2C2C] w-8' : 'bg-gray-300 w-2.5'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>
      {/* Pricing Options */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Casa Completa */}
            <div className="bg-[#F5F2ED] rounded-lg overflow-hidden shadow-lg">
              <div className="p-8">
                <div className="w-16 h-16 bg-[#9B2C2C]/10 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <HomeIcon className="text-[#9B2C2C]" size={32} />
                </div>
                <h3
                  className="text-2xl font-bold mb-2 text-center"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Alquiler de Casa Completa
                </h3>
                <div className="text-center mb-6">
                  <p className="text-5xl font-bold text-[#9B2C2C]">275€</p>
                  <p className="text-[#5A5F4D]">/noche</p>
                </div>
                <p className="text-center text-[#5A5F4D] mb-2">Hasta 11 personas</p>
                <p className="text-center text-[#5A5F4D] mb-8">
                  Uso exclusivo de la casa, con libertad para disfrutar de sus salones históricos
                  y cocina. Desayuno incluido.
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="text-[#9B2C2C] flex-shrink-0 mt-1" size={20} />
                    <span className="text-[#5A5F4D]">Uso exclusivo</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="text-[#9B2C2C] flex-shrink-0 mt-1" size={20} />
                    <span className="text-[#5A5F4D]">Salones históricos</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="text-[#9B2C2C] flex-shrink-0 mt-1" size={20} />
                    <span className="text-[#5A5F4D]">Cocina equipada</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="text-[#9B2C2C] flex-shrink-0 mt-1" size={20} />
                    <span className="text-[#5A5F4D]">Desayuno incluido</span>
                  </li>
                </ul>
                <Link
                  to="/contacto"
                  className="block w-full text-center px-6 py-4 bg-[#9B2C2C] text-white rounded hover:bg-[#7d2323] transition-all"
                >
                  Reservar ahora
                </Link>
              </div>
            </div>

            {/* Por Habitaciones */}
            <div className="bg-[#F5F2ED] rounded-lg overflow-hidden shadow-lg">
              <div className="p-8">
                <div className="w-16 h-16 bg-[#9B2C2C]/10 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <Users className="text-[#9B2C2C]" size={32} />
                </div>
                <h3
                  className="text-2xl font-bold mb-2 text-center"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Alojamiento por Habitaciones
                </h3>
                <div className="text-center mb-6">
                  <p className="text-5xl font-bold text-[#9B2C2C]">70€</p>
                  <p className="text-[#5A5F4D]">/noche</p>
                </div>
                <p className="text-center text-[#5A5F4D] mb-2">
                  2 habitaciones de matrimonio + 3 dobles camas individuales
                </p>
                <p className="text-center text-[#5A5F4D] mb-8">
                  Habitaciones espaciosas que respetan la escala y materiales originales, con suelos
                  de madera encerada y mobiliario de época. Desayuno incluido.
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="text-[#9B2C2C] flex-shrink-0 mt-1" size={20} />
                    <span className="text-[#5A5F4D]">Habitaciones dobles</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="text-[#9B2C2C] flex-shrink-0 mt-1" size={20} />
                    <span className="text-[#5A5F4D]">Suelos de madera encerados</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="text-[#9B2C2C] flex-shrink-0 mt-1" size={20} />
                    <span className="text-[#5A5F4D]">Mobiliario de época</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="text-[#9B2C2C] flex-shrink-0 mt-1" size={20} />
                    <span className="text-[#5A5F4D]">Desayuno incluido</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="text-[#9B2C2C] flex-shrink-0 mt-1" size={20} />
                    <span className="text-[#5A5F4D]">Camas supletorias +20€ (máximo dos por habitación)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="text-[#9B2C2C] flex-shrink-0 mt-1" size={20} />
                    <span className="text-[#5A5F4D]">Grupos a partir de 5 personas, 25e/pers. en habitación doble</span>
                  </li>
                </ul>
                <Link
                  to="/contacto"
                  className="block w-full text-center px-6 py-4 bg-[#9B2C2C] text-white rounded hover:bg-[#7d2323] transition-all"
                >
                  Reservar ahora
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 bg-[#F5F2ED]">
        <div className="container mx-auto px-6">
          <h2
            className="text-3xl font-bold mb-12 text-center"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Características
          </h2>
          <p className="text-center text-lg text-[#5A5F4D] mb-12 max-w-3xl mx-auto">
            Todo lo que necesitas para una estancia perfecta
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {[
              {
                title: 'Exteriores',
                items: [
                  'Terraza de 60m²',
                  'Jardín con huerto',
                  'Zona de aparcamiento',
                  'Vistas a la montaña',
                  'Junto al río',
                  'Cerca de Francia (30 km)',
                ],
              },
              {
                title: 'Zonas Comunes',
                items: [
                  'Salón con chimenea y piano',
                  'Decoración siglo XIX',
                  'Uso de cocina',
                  'Txoko',
                  '2 baños grandes',
                  'Cocina industrial',
                ],
              },
              {
                title: 'Habitaciones',
                items: ['Cama', 'Armario', 'Mesa y silla', 'Ropa de cama', 'Toallas'],
              },
              {
                title: 'Servicios',
                items: [
                  'Desayuno incluido',
                  'WiFi gratuito',
                  'Mascotas (consultar)',
                  'Leña gratis',
                  'Juegos de mesa',
                  'Palas y raquetas',
                ],
              },
            ].map((feature, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-md">
                <h3
                  className="text-xl font-bold mb-4"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {feature.title}
                </h3>
                <ul className="space-y-2">
                  {feature.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 bg-[#9B2C2C] rounded-full mt-2 flex-shrink-0" />
                      <span className="text-sm text-[#5A5F4D]">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <h2
            className="text-3xl font-bold mb-12 text-center"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Opiniones
          </h2>
          <p className="text-center text-lg text-[#5A5F4D] mb-12">Lo que dicen nuestros huéspedes</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-[#F5F2ED] rounded-lg p-6">
              <p className="text-[#5A5F4D] mb-4 italic">
                "La casa está fenomenal y el entorno precioso. La atención de Cristina fue
                excepcional."
              </p>
              <p className="font-semibold">María Carmen</p>
            </div>
            <div className="bg-[#F5F2ED] rounded-lg p-6">
              <p className="text-[#5A5F4D] mb-4 italic">
                "La casa es espectacular, estilo rústico rehabilitado, un lujo alucinante."
              </p>
              <p className="font-semibold">Iosu Ortiz</p>
            </div>
            <div className="bg-[#F5F2ED] rounded-lg p-6">
              <p className="text-[#5A5F4D] mb-4 italic">
                "Unos días maravillosos. Entorno precioso. Casa limpia, grande y con todo lo
                necesario. Cristina es encantadora, amable, simpática y detallista."
              </p>
              <p className="font-semibold">Sandra</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#9B2C2C] text-white">
        <div className="container mx-auto px-6 text-center">
          <h2
            className="text-3xl md:text-4xl font-bold mb-6 text-white"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            ¿Listo para tu experiencia en Graciosenea?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-white/90">
            Reserva sin comisiones. Atención directa y personalizada.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/contacto"
              className="inline-block px-8 py-4 bg-white text-[#9B2C2C] rounded hover:bg-gray-100 transition-all"
            >
              Solicita tu estancia
            </Link>
            <a
              href="tel:623151160"
              className="inline-block px-8 py-4 border-2 border-white text-white rounded hover:bg-white hover:text-[#9B2C2C] transition-all"
            >
              Llámanos: 623 15 11 60 / 623 21 60 31
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Turismo;